import { StudioImageType } from "api/studio/studio.type";

// Studio edit-result `edit_type` (as reported by the socket/edit-complete payload, or by a notification's
// `target.edit_type` — REF-6306) -> StudioImageType. Shared between StudioEditImageModal (session edit
// results) and StudioProjectDetailContext (notification deep-link focus resolution) so there is exactly
// one source of truth instead of two maps drifting apart.
export const EDIT_TYPE_TO_IMAGE_TYPE: Record<string, StudioImageType> = {
  design_edits: "design_edit",
  background_edits: "background_edit",
  glam_edits: "glam_edit",
  pose_edits: "pose_edit",
  pose_variants: "pose_variant",
  video_edits: "video_edit",
  crop_edits: "crop_edit",
  adjust_edits: "adjust_edit",
  overlay_edits: "overlay_edit",
  face_fixes: "face_fix",
  product_fixes: "product_fix",
  retouch_defects_edits: "retouch_defects",
  fix_garment_texture_edits: "fix_garment_texture",
  expand_image_edits: "expand_image",
  // REF-6367 — a single-frame polish started from the edit panel arrives as `design_type: "studio"` +
  // `edit_type: "polish_edits"`, i.e. straight down the channel the edit modal already listens on. This
  // row is the only FE wiring that path needs.
  polish_edits: "polish",
};

// The WS `design_type`s a studio EDIT completion arrives as. Shared by the editor's own monitor
// (EditDesignWithStudioContext) and the project page, which refetches on them so a result lands even
// after the modal that started the edit was closed. Never add `studio_project` here — that is the
// project-generation frame, and the editor would read it as its own edit finishing
// ([STUDIO_JOB_EVENT_TYPES]).
export const STUDIO_EDIT_DESIGN_TYPES = ["studio", "background_edits", "pose_edits", "glam_edits", "video_edits", "face_fixes", "product_fixes", "expand_image_edits"] as const;

// Builds the fully-namespaced i18n keys for one rotating-copy category shown by GeneratingCard,
// e.g. "photoshoot:studio_generating.background.1" .. ".4". Shared between StudioEditImageModal
// (keyed by the active edit TOOL, known only while a new edit is being composed) and
// StudioProjectDetail's list cards (keyed by the persisted/reported `edit_type` string, since the
// tool itself isn't available once an edit is only a `pending_edits` row or a session-cache entry)
// — REF-6306.
export const buildStudioStageKeys = (category: string): string[] => [1, 2, 3, 4].map((n) => `photoshoot:studio_generating.${category}.${n}`);

export const STUDIO_GENERIC_STAGE_KEYS = buildStudioStageKeys("generic");

// `edit_type` (as reported by BE `pending_edits[].edit_type` or a session-cached pending edit) ->
// GeneratingCard stage-copy category. Only the categories with dedicated copy are listed; anything
// else (design_edits, product_fixes, retouch_defects_edits, crop/adjust/overlay_edits, …) falls back
// to the generic category, same as StudioEditImageModal's ToolType map.
const EDIT_TYPE_TO_STAGE_CATEGORY: Partial<Record<string, string>> = {
  background_edits: "background",
  glam_edits: "glam",
  pose_edits: "pose",
  video_edits: "video",
  face_fixes: "face_fix",
  fix_garment_texture_edits: "fix_texture",
  expand_image_edits: "expand",
};

export const getStudioStageKeysByEditType = (editType?: string): string[] => {
  const category = editType ? EDIT_TYPE_TO_STAGE_CATEGORY[editType] : undefined;

  return category ? buildStudioStageKeys(category) : STUDIO_GENERIC_STAGE_KEYS;
};

// REF-6306/REF-6203 §3 — `pending_edits[].started_at` comes in TWO shapes: job_queue jobs return an
// offset-bearing ISO8601 (`…+00:00`), while sirius jobs return a NAIVE UTC ISO8601 with no offset.
// `new Date(naive)` / `Date.parse(naive)` would treat the naive one as LOCAL time (wrong elapsed → wrong
// %). Normalize: if there's no `Z`/`±HH:MM` offset, it's UTC — append `Z` before parsing.
export const parseStudioStartedAt = (value?: string): number => {
  if (!value) return Date.now();
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value.trim());
  const parsed = Date.parse(hasTimezone ? value : `${value}Z`);

  return Number.isNaN(parsed) ? Date.now() : parsed;
};

export default EDIT_TYPE_TO_IMAGE_TYPE;
